// SEO Analysis for Redirects
const BASE_DOMAIN = 'https://www.willscot.com';

// Calculate SEO score for a redirect or target URL
export const calculateSEOScore = (redirect, redirectsData = [], context = {}) => {
  let score = 100; // Start with perfect score
  const issues = [];
  const recommendations = [];

  // 1. Redirect Status Code Analysis (25 points)
  if (redirect.status === '301') {
    // Perfect - permanent redirect
    issues.push({ type: 'positive', message: 'Using 301 (permanent) redirect', impact: 0 });
  } else if (redirect.status === '302') {
    score -= 15;
    issues.push({ type: 'warning', message: 'Using 302 (temporary) redirect', impact: 15 });
    recommendations.push('Consider using 301 for permanent redirects to pass link equity');
  } else if (redirect.status === '404') {
    score -= 25;
    issues.push({ type: 'error', message: 'Returns 404 (not found)', impact: 25 });
    recommendations.push('Fix broken redirects - 404s hurt SEO ranking');
  } else {
    score -= 20;
    issues.push({ type: 'warning', message: `Unusual status code: ${redirect.status}`, impact: 20 });
  }

  // 2. Target URL Analysis (25 points)
  const target = redirect.target;
  
  // Check if target is external
  if (!target.startsWith(BASE_DOMAIN) && !target.startsWith('/')) {
    score -= 10;
    issues.push({ type: 'warning', message: 'External redirect (potential link equity loss)', impact: 10 });
    recommendations.push('Consider if external redirect is necessary for SEO');
  }

  // Check for HTTPS
  if (target.startsWith('http://')) {
    score -= 15;
    issues.push({ type: 'error', message: 'Redirecting to HTTP (not secure)', impact: 15 });
    recommendations.push('Always redirect to HTTPS for better SEO');
  } else if (target.startsWith('https://') || target.startsWith('/')) {
    issues.push({ type: 'positive', message: 'Secure target URL', impact: 0 });
  }

  // Check for URL parameters that might indicate tracking issues
  if (target.includes('utm_')) {
    score -= 5;
    issues.push({ type: 'info', message: 'Contains UTM tracking parameters', impact: 5 });
    recommendations.push('Consider if UTM parameters are necessary for internal redirects');
  }

  // 3. Redirect Chain Analysis (25 points)
  if (redirectsData.length > 0) {
    const chainLength = getRedirectChainLength(redirect.source, redirectsData);
    if (chainLength > 1) {
      const penalty = Math.min(chainLength * 5, 25);
      score -= penalty;
      issues.push({ 
        type: 'warning', 
        message: `Part of ${chainLength}-step redirect chain`, 
        impact: penalty 
      });
      recommendations.push('Reduce redirect chains - each hop loses ~5% link equity');
    } else {
      issues.push({ type: 'positive', message: 'Direct redirect (no chain)', impact: 0 });
    }
  }

  // 4. URL Structure Analysis (25 points)
  const source = redirect.source;
  
  // Check for proper URL structure
  if (source.includes('//')) {
    score -= 10;
    issues.push({ type: 'error', message: 'Double slashes in source URL', impact: 10 });
    recommendations.push('Fix URL structure - double slashes can cause crawling issues');
  }

  // Check for file extensions that might indicate old static pages
  if (source.match(/\.(html|htm|php|asp|aspx)$/)) {
    issues.push({ type: 'info', message: 'Redirecting legacy file extension', impact: 0 });
    recommendations.push('Good practice - redirecting old file-based URLs');
  }

  // Check for language/locale patterns
  if (source.match(/^\/[a-z]{2}(\/|$)/)) {
    issues.push({ type: 'positive', message: 'Proper locale URL structure', impact: 0 });
  }

  // Check for overly long URLs
  if (source.length > 100) {
    score -= 5;
    issues.push({ type: 'warning', message: 'Long source URL (>100 chars)', impact: 5 });
    recommendations.push('Shorter URLs are better for SEO and user experience');
  }

  // Additional context-based analysis
  if (context.isDuplicate) {
    score -= 10;
    issues.push({ type: 'warning', message: 'Multiple sources redirect to this target', impact: 10 });
    recommendations.push('Consider consolidating multiple redirects to avoid confusion');
  }

  if (context.hasCycle) {
    score -= 30;
    issues.push({ type: 'error', message: 'Part of redirect cycle', impact: 30 });
    recommendations.push('Fix redirect cycles immediately - they prevent proper indexing');
  }

  // Ensure score doesn't go below 0
  score = Math.max(0, score);

  return {
    score: Math.round(score),
    grade: getGrade(score),
    issues,
    recommendations: [...new Set(recommendations)] // Remove duplicates
  };
};

// Get redirect chain length starting from a source
const getRedirectChainLength = (startSource, redirectsData) => {
  const redirectMap = new Map();
  redirectsData.forEach(redirect => {
    if (redirect.source && redirect.target) {
      redirectMap.set(redirect.source, redirect.target);
    }
  });

  let current = startSource;
  let length = 0;
  const visited = new Set();

  while (current && redirectMap.has(current)) {
    if (visited.has(current)) break; // Cycle detected
    visited.add(current);
    length++;
    current = redirectMap.get(current);
    
    // Stop if target is external
    if (current && !current.startsWith('/') && !current.startsWith(BASE_DOMAIN)) {
      break;
    }
  }

  return length;
};

// Convert score to letter grade
const getGrade = (score) => {
  if (score >= 90) return 'A';
  if (score >= 80) return 'B';
  if (score >= 70) return 'C';
  if (score >= 60) return 'D';
  return 'F';
};

// Get color for score visualization
export const getScoreColor = (score) => {
  if (score >= 90) return '#28a745'; // Green
  if (score >= 80) return '#6ab04c'; // Light green
  if (score >= 70) return '#f39c12'; // Orange
  if (score >= 60) return '#e67e22'; // Dark orange
  return '#e74c3c'; // Red
};

// Analyze SEO scores for duplicate redirects
export const analyzeDuplicatesSEO = (duplicates, redirectsData) => {
  return duplicates.map(duplicate => {
    const targetScores = duplicate.sources.map(source => 
      calculateSEOScore(source, redirectsData, { isDuplicate: true })
    );
    
    const avgScore = targetScores.reduce((sum, analysis) => sum + analysis.score, 0) / targetScores.length;
    const worstScore = Math.min(...targetScores.map(analysis => analysis.score));
    const bestScore = Math.max(...targetScores.map(analysis => analysis.score));
    
    const allIssues = targetScores.flatMap(analysis => analysis.issues);
    const allRecommendations = [...new Set(targetScores.flatMap(analysis => analysis.recommendations))];

    return {
      ...duplicate,
      seoAnalysis: {
        averageScore: Math.round(avgScore),
        worstScore,
        bestScore,
        grade: getGrade(avgScore),
        sourceAnalyses: targetScores,
        commonIssues: getCommonIssues(allIssues),
        recommendations: allRecommendations
      }
    };
  });
};

// Analyze SEO scores for nested redirects
export const analyzeNestedSEO = (nestedRedirects, redirectsData) => {
  return nestedRedirects.map(redirectChain => {
    const chainScores = redirectChain.chain.map(step => 
      calculateSEOScore(step.original, redirectsData, { 
        hasCycle: redirectChain.hasCycle,
        chainLength: redirectChain.length 
      })
    );
    
    // Chain score is based on the worst score in the chain
    const chainScore = Math.min(...chainScores.map(analysis => analysis.score));
    
    // Additional penalty for chain length
    const lengthPenalty = Math.min((redirectChain.length - 1) * 10, 50);
    const finalScore = Math.max(0, chainScore - lengthPenalty);
    
    const allIssues = chainScores.flatMap(analysis => analysis.issues);
    const allRecommendations = [...new Set(chainScores.flatMap(analysis => analysis.recommendations))];
    
    if (redirectChain.length > 2) {
      allRecommendations.push('Reduce redirect chain length to improve SEO performance');
    }

    return {
      ...redirectChain,
      seoAnalysis: {
        chainScore: Math.round(finalScore),
        grade: getGrade(finalScore),
        stepAnalyses: chainScores,
        lengthPenalty,
        commonIssues: getCommonIssues(allIssues),
        recommendations: allRecommendations
      }
    };
  });
};

// Find common issues across multiple analyses
const getCommonIssues = (issues) => {
  const issueCount = {};
  issues.forEach(issue => {
    const key = issue.message;
    issueCount[key] = (issueCount[key] || 0) + 1;
  });
  
  return Object.entries(issueCount)
    .filter(([_, count]) => count > 1)
    .map(([message, count]) => ({ message, count }))
    .sort((a, b) => b.count - a.count);
}; 